import { Box, Checkbox, FormControlLabel, Paper } from '@mui/material';
import type { LocationSummary } from '../../../networkRequest/locationsService';
import { FilterState } from './filterState';

type LocationFilterPanelProps = {
  locationFilters: LocationSummary[];
  filterState: FilterState;
  onFilterChange: (newState: FilterState) => void;
};

const LocationFilterPanel: React.FC<LocationFilterPanelProps> = ({ locationFilters, filterState, onFilterChange }) => {
  const toggleGroup = (checkpoints: { name: string; id: number }[]) => {
    const newCheckpointIds = { ...filterState.checkpointIds };
    const allChecked = checkpoints.every(c => newCheckpointIds[c.id]);
    if (allChecked) {
      checkpoints.forEach(c => { delete newCheckpointIds[c.id]; });
    } else {
      checkpoints.forEach(c => { newCheckpointIds[c.id] = true; });
    }
    onFilterChange({ ...filterState, checkpointIds: newCheckpointIds });
  };

  const toggleCheckpoint = (id: number) => {
    const newCheckpointIds = { ...filterState.checkpointIds };
    if (newCheckpointIds[id]) {
      delete newCheckpointIds[id];
    } else {
      newCheckpointIds[id] = true;
    }
    onFilterChange({ ...filterState, checkpointIds: newCheckpointIds });
  };

  // Locations with no checkpoints can't be filtered on (the model only tracks
  // checkpoint selections), so we omit them rather than show a checkbox that's
  // vacuously checked and can't be toggled.
  const groupsWithCheckpoints = locationFilters.filter(group => group.checkpoints.length > 0);

  return (
    <Paper sx={{ pl: 2, pt: 1, pb: 1, minWidth: 200 }}>
      {groupsWithCheckpoints.map(group => (
        <Box key={group.name}>
          <FormControlLabel
            label={<strong>{group.name}</strong>}
            control={
              <Checkbox
                size="small"
                checked={group.checkpoints.every(c => filterState.checkpointIds[c.id])}
                indeterminate={
                  group.checkpoints.some(c => filterState.checkpointIds[c.id]) &&
                  !group.checkpoints.every(c => filterState.checkpointIds[c.id])
                }
                onChange={() => toggleGroup(group.checkpoints)}
                // Checkboxes toggle on Space natively; also honor Enter. Toggling
                // a group selects/deselects all of its checkpoints.
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    toggleGroup(group.checkpoints);
                  }
                }}
              />
            }
          />
          {group.checkpoints.map(child => (
            <Box key={child.name} sx={{ ml: 3 }}>
              <FormControlLabel
                label={child.name}
                control={
                  <Checkbox
                    size="small"
                    checked={!!filterState.checkpointIds[child.id]}
                    onChange={() => toggleCheckpoint(child.id)}
                    // Checkboxes toggle on Space natively; also honor Enter.
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        toggleCheckpoint(child.id);
                      }
                    }}
                  />
                }
              />
            </Box>
          ))}
        </Box>
      ))}
    </Paper>
  );
};

export default LocationFilterPanel;
